import React from 'react';
import { Box, TableCell, TableRow, Typography } from '@mui/material';
import { Helpers } from '../../../../common/helpers/helpers';
import { EmployeeDocRequirement, LibraryItem, LibraryListRowProps } from './types';
import { useAppSelector } from '../../../../common/hooks/useAppSelector';
import { selectAllCompanies } from '../../../../slices/companies/companiesSlice';
import { selectAllDocuments } from '../../../../slices/documents/documentsSlice';
import { useTranslation } from 'react-i18next';
import { useLibraryFunctions } from '../../hooks/useLibraryFunctions';
import styles from './LibraryList.module.scss';
import InfoIcon from '@mui/icons-material/Info';
import TemplatePreview from '../template-preview/TemplatePreview';
import TemplatePreviewTooltip from '../template-preview-tooltip/TemplatePreviewTooltip';

type RequirementVariant = 'green' | 'blue' | 'orange' | 'purple' | 'gray';

const REQUIREMENT_CONFIG: Record<
  EmployeeDocRequirement,
  { labelKey: string; variant: RequirementVariant }
> = {
  all: { labelKey: 'employeeDocRequirements.all', variant: 'green' },
  casual: { labelKey: 'employeeDocRequirements.casual', variant: 'blue' },
  fixed_term: { labelKey: 'employeeDocRequirements.fixedTerm', variant: 'orange' },
  part_time: { labelKey: 'employeeDocRequirements.partTime', variant: 'blue' },
  full_time: { labelKey: 'employeeDocRequirements.fullTime', variant: 'blue' },
  recommended: { labelKey: 'employeeDocRequirements.recommended', variant: 'purple' },
  help_guide: { labelKey: 'employeeDocRequirements.helpGuide', variant: 'gray' },
  as_required: { labelKey: 'employeeDocRequirements.asRequired', variant: 'gray' },
};

const REQUIREMENT_VARIANT_CLASSES: Record<RequirementVariant, string> = {
  green: styles.requirement_badge_green,
  blue: styles.requirement_badge_blue,
  orange: styles.requirement_badge_orange,
  purple: styles.requirement_badge_purple,
  gray: styles.requirement_badge_gray,
};

export const LibraryListRow = ({
  item,
  renderActionButtons,
  showRequirement,
  ownTemplate,
}: LibraryListRowProps) => {
  const company = useAppSelector(selectAllCompanies)[0];
  const { t } = useTranslation();
  const documentUploads = useAppSelector(selectAllDocuments);
  const freePlan =
    (!company?.stripe_product_id && !company?.plan_settings.stripe_product_id) ||
    company?.stripe_product_name?.includes('eSign');
  const { setPlanUpgradingMessage, navigateToTemplatePage, handleS3DownloadLink } =
    useLibraryFunctions();
  const handleNavigate = (item: LibraryItem) => {
    const actionAvailableFree = 'is_free' in item && item.is_free;
    if (item.download_link) {
      handleS3DownloadLink(item.id, 'name' in item ? item.name : '');
    } else {
      if (!('region' in item)) {
        navigateToTemplatePage('company-templates', item.id);
      } else {
        if (freePlan) {
          if (actionAvailableFree) {
            navigateToTemplatePage(item.region ?? 'company-templates', item.id);
          } else {
            setPlanUpgradingMessage(true);
          }
        } else {
          navigateToTemplatePage(item.region ?? 'company-templates', item.id);
        }
      }
    }
  };

  const requirement = item.employee_doc_requirements
    ? REQUIREMENT_CONFIG[item.employee_doc_requirements as EmployeeDocRequirement]
    : undefined;
  const isRequiredForEveryone = showRequirement && item.employee_doc_requirements === 'all';

  const documentTypes = ('dl_types' in item ? item.dl_types : item.dt_types) ?? [];
  const documentTypeText = documentTypes.length > 0 ? documentTypes.join(', ') : '';
  // Same value the former "Date created/modified" column showed ('2026' is a placeholder for
  // public library items, which have no dates).
  const dateText = 'updated' in item ? Helpers.timeStampToDateV2(item.created) : '2026';

  return (
    <TableRow
      className={`${styles.table_row}${isRequiredForEveryone ? ` ${styles.table_row_required}` : ''}`}
    >
      <TableCell
        id={`libraryListRowHeaderDefaultCell_${item.id}`}
        className={styles.table_cell_title_container}
        onClick={() => handleNavigate(item)}
      >
        <Box
          id={`libraryListRowHeaderDefaultCellBox_${item.id}`}
          className={styles.profile_container}
        >
          <div
            id={`libraryListRowHeaderDefaultCellDiv_${item.id}`}
            className={styles.title_container}
          >
            <Typography
              id={`libraryListRowHeaderDefaultCellText_${item.id}`}
              variant='body2'
              className={`${styles.profile_text}${
                isRequiredForEveryone ? ` ${styles.profile_text_required}` : ''
              }`}
            >
              {item.name
                ? item.name
                : documentUploads.find((el) => el.id === item.document_upload)?.name ||
                  t('templatePage.headerDefault')}
            </Typography>
            {/* Same rule as before: the Free label shows only for free items to companies
                without a subscription / plan, right after the template name. */}
            {item.is_free && !company?.stripe_subscription && !company?.stripe_product_id && (
              <Box
                id={`libraryListRowHeaderDefaultCellBox2_${item.id}`}
                className={styles.free_tag}
              >
                {t('pricingOptionNames.free')}
              </Box>
            )}
          </div>
          <Typography
            id={`libraryListRowSubtitleText_${item.id}`}
            variant='body2'
            className={styles.subtitle_text}
          >
            {documentTypeText && (
              <>
                <span id={`libraryListRowNavigateCell3Text_${item.id}`}>{documentTypeText}</span>
                <span className={styles.subtitle_separator} aria-hidden='true'>
                  &middot;
                </span>
              </>
            )}
            <span id={`libraryListRow2026CellText_${item.id}`}>
              {t('libraryTable.updatedDate', { date: dateText })}
            </span>
          </Typography>
        </Box>
      </TableCell>
      {showRequirement && (
        <TableCell
          id={`libraryListRowRequirementCell_${item.id}`}
          className={`${styles.table_cell} ${styles.requirement_cell}`}
          onClick={() => handleNavigate(item)}
        >
          {requirement ? (
            <Typography
              id={`libraryListRowRequirementCellText_${item.id}`}
              component='span'
              className={`${styles.requirement_badge} ${
                REQUIREMENT_VARIANT_CLASSES[requirement.variant]
              }`}
            >
              {t(requirement.labelKey)}
            </Typography>
          ) : (
            <Typography
              id={`libraryListRowRequirementCellText_${item.id}`}
              variant='body2'
              className={styles.cell_text}
            >
              -
            </Typography>
          )}
        </TableCell>
      )}
      <TableCell className={`${styles.table_cell} ${styles.actions_cell}`}>
        <Box id={`libraryListRowActionsBox_${item.id}`} className={styles.row_actions}>
          {renderActionButtons(item)}
          {/* The info icon closes the row (as in the mockup). Rows without a description keep an
              empty slot of the same size, so the action icons stay aligned from row to row.
              My Templates have no info bubbles, so their rows have no slot at all. */}
          {!ownTemplate && (
            <Box id={`libraryListRowInfoSlot_${item.id}`} className={styles.info_slot}>
              {item.description && (
                <TemplatePreviewTooltip
                  title={
                    <TemplatePreview
                      id={`libraryListRowTemplatePreview_${item.id}`}
                      description={item.description}
                      fines={item.fines ?? undefined}
                      resolution={item.resolution ?? undefined}
                    />
                  }
                  arrow
                  placement='top'
                >
                  <InfoIcon
                    id={`libraryListRowHeaderDefaultCellInfoIcon_${item.id}`}
                    color='primary'
                    fontSize='small'
                  />
                </TemplatePreviewTooltip>
              )}
            </Box>
          )}
        </Box>
      </TableCell>
    </TableRow>
  );
};
